import type { LmiCostRuleSet, RefundOnDischargeRuleSet } from "./types.js";

// LMI rulesets for FY2025-26 (period-tagged indicative snapshot).
//
// ⚠️ INDICATIVE GRID — READ THE MODULE HEADER (types.ts). Unlike AU-06/08/13
// (ATO/APRA/Services Australia statutory tables), the LMI premium grid has NO
// authoritative public statutory source. It reprices opaquely on insurer
// schedules we can't observe, and the real spread between lender grids is up to
// ~2× (data-critic ledger #1/#2). Every rate here is therefore an INDICATIVE
// RANGE, snapshotted + dated, and ships only with the "indicative — your
// lender's actual LMI may differ" stamp. NEVER present it as a quote.
//
// Premium-by-LVR cliffs (ledger #3, CITED from a real homeloanexperts rate grid):
//   80%      LVR : nil (LMI threshold — the 80% cliff)
//   80–85%   LVR : ~0.5–1.0% of loan
//   85–90%   LVR : ~0.876–1.463% of loan  (table: 0.876–1.46%)
//   90–95%   LVR : ~1.463–2.516% of loan  (table: 1.46–2.52%)
//   95%+     LVR : ~2.609–4.613% of loan  (table: 2.61–4.61%)
// Discrete cliffs confirmed (e.g. crossing 90%→91% jumps ~2.18%→3.51%). The
// dossier's "~1% @85 → ~3–5% @95" framing is accurate/conservative vs this grid.
//
// Sense-check against the indicative headline worked examples (ledger #1/#2,
// 🟡 indicative, Westpac/QLD basis — NOT universal premiums):
//   $500k @ 95% LVR (5% deposit)  → money.com.au $17,028 ; cross-check $17,476.
//     Loan $475k × 2.61–4.61% = $12,396–$21,898 → both headline figures land
//     inside the indicative band. (We never assert a single $17,028 "quote".)
//   $1.0m @ 90% LVR (10% deposit) → money.com.au $40,135 (Westpac) vs ~$22,644
//     (standard-doc table) — a ~2× spread. Loan $900k × 1.46–2.52% = $13,167–
//     $22,644; the Westpac $40,135 sits ABOVE even this band, which is itself
//     the proof the number must never be a quote.
//
// Never edit a shipped ruleset to "fix" a later period — author a new dated
// snapshot and register it. Re-snapshot the grid from the Helia estimator +
// lender fact sheets each period (the indicative-grid maintenance plan).
export const lmiCostFy2025_26: LmiCostRuleSet = {
  product: "AU08", // shared Product union; LMI ships under the property-buyer suite.
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    premiumGrid:
      "https://www.homeloanexperts.com.au/lenders-mortgage-insurance/lmi-premium-rates/",
    workedExamples: "https://www.money.com.au/home-loans/lenders-mortgage-insurance",
    lmiThreshold:
      "https://moneysmart.gov.au/glossary/lenders-mortgage-insurance-lmi",
    firstHomeGuarantee:
      "https://www.housingaustralia.gov.au/media/expanded-australian-government-5-deposit-scheme-support-more-australians-home-ownership",
  },
  sourcesVerifiedAt: "2026-06-22",
  lmiThresholdLvr: 0.8,
  tiers: [
    // Below 80% LVR — no LMI.
    {
      fromLvr: 0,
      toLvr: 0.8,
      ratePctLow: 0,
      ratePctHigh: 0,
      label: "Below 80% LVR",
    },
    // 80–85% LVR cliff band.
    {
      fromLvr: 0.8,
      toLvr: 0.85,
      ratePctLow: 0.005,
      ratePctHigh: 0.01,
      label: "80–85% LVR",
    },
    // 85–90% LVR (grid: 0.876–1.463%).
    {
      fromLvr: 0.85,
      toLvr: 0.9,
      ratePctLow: 0.00876,
      ratePctHigh: 0.01463,
      label: "85–90% LVR",
    },
    // 90–95% LVR (grid: 1.463–2.516%).
    {
      fromLvr: 0.9,
      toLvr: 0.95,
      ratePctLow: 0.01463,
      ratePctHigh: 0.02516,
      label: "90–95% LVR",
    },
    // 95%+ LVR top tier (grid: 2.609–4.613%).
    {
      fromLvr: 0.95,
      toLvr: null,
      ratePctLow: 0.02609,
      ratePctHigh: 0.04613,
      label: "95%+ LVR",
    },
  ],
};

// Refund-on-early-discharge scale (period-tagged, INDICATIVE, disqualifier-led).
//
// CURRENCY-CRITICAL (refund ledger #3, CITED 🟢): Helia (historically the
// largest insurer) has DISCONTINUED LMI refunds entirely — only an internal
// -refinance discount survives. Any user insured via Helia/CommBank is owed
// NOTHING. The tool LEADS with this disqualifier and must never quote a figure.
//
// Where refunds survive (QBE/Westpac/ANZ books) they are insurer/lender
// -specific, vintage-conditional and DECLINING, and require: FULL discharge
// (partial disqualifies), within a tight window, with lender notice ≤~30 days,
// minus non-refundable stamp duty, and the borrower must PROACTIVELY ask.
//
// Indicative scale (ledger #1/#2, 🟡 indicative — Westpac basis; ANZ ~50%/25%):
//   ≤12 months  : ~40% of premium (minus stamp duty)
//   12–24 months: ~20% of premium (minus stamp duty)
//   >24 months  : nil
// Encoded as ranges (insurers differ: ~40–50% / ~20–25%) so we never assert a
// single owed figure.
export const refundOnDischargeFy2025_26: RefundOnDischargeRuleSet = {
  product: "AU08",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    refundScale:
      "https://www.homeloanexperts.com.au/lenders-mortgage-insurance/lmi-refund/",
    heliaDiscontinued:
      "https://www.yourmortgage.com.au/compare-home-loans/lmi-refund",
    qbeConditions: "https://www.qbe.com/lmi/borrowers/frequently-asked-questions",
  },
  sourcesVerifiedAt: "2026-06-22",
  refundsDiscontinuedInsurers: ["helia"],
  lenderNoticeWindowDays: 30,
  tiers: [
    // ≤12 months: ~40–50% (insurer-specific).
    {
      fromMonths: 0,
      toMonths: 12,
      refundPctLow: 0.4,
      refundPctHigh: 0.5,
      label: "≤12 months",
    },
    // 12–24 months: ~20–25%.
    {
      fromMonths: 12,
      toMonths: 24,
      refundPctLow: 0.2,
      refundPctHigh: 0.25,
      label: "12–24 months",
    },
    // >24 months: nil.
    {
      fromMonths: 24,
      toMonths: null,
      refundPctLow: 0,
      refundPctHigh: 0,
      label: "over 24 months",
    },
  ],
  // Indicative deduction for non-refundable stamp duty on the premium (~10%).
  stampDutyDeductionPctIndicative: 0.1,
};
